import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * CREEP — chibi kawaii black cat with:
 *  - Large triangular ears with violet inner
 *  - Six-point Sharingan star on the forehead
 *  - Huge red eyes with tomoe + slit pupil
 *  - Fluffy chest tuft
 *  - Purple beaded necklace with spiral pendant
 *  - Twin curly tails (nekomata)
 *  - Twin purple flame aura tongues
 *
 * Fully SVG. Animations are CSS-driven for GPU performance.
 * Poses: "idle" | "happy" | "bored" | "sleep" | "chase" | "shake"
 */
export const CreepCharacter = ({
  size = 320,
  pose = "idle",
  accessories = [],
  onTap,
  reactionKey = 0,
  className = "",
}) => {
  const [reaction, setReaction] = useState(null); // "meow" | "purr"
  const [ripples, setRipples] = useState([]);
  const [particles, setParticles] = useState(() => buildParticles());
  const rootRef = useRef(null);

  useEffect(() => {
    if (!reactionKey) return;
    setReaction("meow");
    setParticles(buildParticles(true));
    const t = setTimeout(() => setReaction(null), 900);
    return () => clearTimeout(t);
  }, [reactionKey]);

  const handleTap = (e) => {
    e.stopPropagation();
    const rect = rootRef.current?.getBoundingClientRect();
    if (rect) {
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      const id = Date.now();
      setRipples((r) => [...r, { id, x, y }]);
      setTimeout(() => setRipples((r) => r.filter((x) => x.id !== id)), 700);
    }
    setReaction("meow");
    setParticles(buildParticles(true));
    setTimeout(() => setReaction(null), 900);
    onTap?.();
  };

  const poseAnim = useMemo(() => {
    switch (pose) {
      case "happy":
        return { breath: "1.6s", blink: "2.4s", tailDur: "1.1s", eyeGlow: 1, bodyAnim: "creep-happy 1.8s ease-in-out infinite" };
      case "bored":
        return { breath: "5.2s", blink: "6.5s", tailDur: "2.8s", eyeGlow: 0.55, bodyAnim: "creep-breathe 5.2s ease-in-out infinite" };
      case "sleep":
        return { breath: "5.8s", blink: "9999s", tailDur: "5.2s", eyeGlow: 0.12, bodyAnim: "creep-breathe 5.8s ease-in-out infinite" };
      case "chase":
        return { breath: "1.2s", blink: "2s", tailDur: "0.5s", eyeGlow: 1, bodyAnim: "creep-chase 0.8s ease-in-out infinite" };
      case "shake":
        return { breath: "3s", blink: "3s", tailDur: "0.6s", eyeGlow: 1, bodyAnim: "creep-shake 0.55s ease-in-out infinite" };
      default:
        return { breath: "3.4s", blink: "4.5s", tailDur: "1.8s", eyeGlow: 0.9, bodyAnim: "creep-breathe 3.4s ease-in-out infinite" };
    }
  }, [pose]);

  const eyesShut = pose === "sleep";
  const showZzz = pose === "sleep";

  return (
    <div
      ref={rootRef}
      className={`relative select-none ${className}`}
      style={{ width: size, height: size }}
      data-testid="creep-character"
      onClick={handleTap}
    >
      {/* Radial aura back */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background:
              "radial-gradient(closest-side, rgba(91,46,255,0.42), rgba(42,18,74,0.28) 45%, transparent 75%)",
            filter: "blur(22px)",
            animation: "creep-breathe 3.8s ease-in-out infinite",
          }}
        />
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 pointer-events-none">
        {particles.map((p, i) => (
          <span
            key={i}
            className="absolute rounded-full"
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: p.s,
              height: p.s,
              background: p.c,
              boxShadow: `0 0 ${p.s * 3}px ${p.c}`,
              opacity: p.o,
              animation: `creep-float-${p.dir} ${p.d}s ${p.delay}s ease-in-out infinite`,
            }}
          />
        ))}
      </div>

      {/* Tap ripples */}
      {ripples.map((r) => (
        <span
          key={r.id}
          className="absolute pointer-events-none rounded-full"
          style={{
            left: `${r.x}%`,
            top: `${r.y}%`,
            width: 12,
            height: 12,
            marginLeft: -6,
            marginTop: -6,
            border: "2px solid rgba(214,0,47,0.85)",
            animation: "creep-ripple 0.7s ease-out forwards",
          }}
        />
      ))}

      {/* Character SVG */}
      <svg
        viewBox="0 0 400 400"
        className="relative w-full h-full cursor-pointer active:scale-[0.97] transition-transform duration-150"
        onClick={handleTap}
        role="img"
        aria-label="Creep, tu gato místico"
        style={{ overflow: "visible" }}
      >
        <defs>
          <radialGradient id="body-grad" cx="50%" cy="42%" r="60%">
            <stop offset="0%" stopColor="#1c1830" />
            <stop offset="65%" stopColor="#0a0714" />
            <stop offset="100%" stopColor="#02010a" />
          </radialGradient>
          <linearGradient id="ear-inner" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#5B2EFF" />
            <stop offset="100%" stopColor="#2A124A" />
          </linearGradient>
          <radialGradient id="red-eye" cx="45%" cy="42%" r="65%">
            <stop offset="0%" stopColor="#ff6b7a" />
            <stop offset="30%" stopColor="#ff2340" />
            <stop offset="70%" stopColor="#D6002F" />
            <stop offset="100%" stopColor="#4a0014" />
          </radialGradient>
          <radialGradient id="eye-glow" cx="50%" cy="50%" r="55%">
            <stop offset="0%" stopColor="rgba(214,0,47,0.85)" />
            <stop offset="100%" stopColor="rgba(214,0,47,0)" />
          </radialGradient>
          <linearGradient id="flame" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#8A6BFF" />
            <stop offset="55%" stopColor="#5B2EFF" />
            <stop offset="100%" stopColor="#2A124A" />
          </linearGradient>
          <linearGradient id="pendant" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#e5e7eb" />
            <stop offset="55%" stopColor="#94a3b8" />
            <stop offset="100%" stopColor="#475569" />
          </linearGradient>
          <linearGradient id="scarf-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#8A6BFF" />
            <stop offset="100%" stopColor="#5B2EFF" />
          </linearGradient>
          <linearGradient id="board-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#8A6BFF" />
            <stop offset="100%" stopColor="#5B2EFF" />
          </linearGradient>
          <filter id="soft-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* -------------------- Flame Aura -------------------- */}
        <g className="flame-aura" style={{ animation: "creep-flame 2.4s ease-in-out infinite" }}>
          {/* Left flames */}
          <g style={{ transformOrigin: "70px 220px" }}>
            <path
              d="M60 260 C 30 220, 30 170, 60 120 C 55 165, 80 180, 78 210 C 96 200, 106 230, 90 260 Z"
              fill="url(#flame)"
              opacity="0.85"
              style={{ filter: "drop-shadow(0 0 10px rgba(91,46,255,0.75))" }}
            />
            <path
              d="M90 260 C 76 240, 82 210, 105 200 C 100 220, 116 232, 108 258 Z"
              fill="url(#flame)"
              opacity="0.9"
            />
            <path
              d="M110 250 C 100 232, 106 210, 128 202 C 122 220, 138 232, 124 250 Z"
              fill="url(#flame)"
              opacity="0.75"
            />
            <path
              d="M45 200 C 30 180, 34 150, 60 145 C 52 165, 66 178, 58 202 Z"
              fill="url(#flame)"
              opacity="0.6"
              style={{ animation: "creep-flame-a 2.6s ease-in-out infinite" }}
            />
          </g>
          {/* Right flames (mirror) */}
          <g style={{ transformOrigin: "330px 220px" }}>
            <path
              d="M340 260 C 370 220, 370 170, 340 120 C 345 165, 320 180, 322 210 C 304 200, 294 230, 310 260 Z"
              fill="url(#flame)"
              opacity="0.85"
              style={{ filter: "drop-shadow(0 0 10px rgba(91,46,255,0.75))" }}
            />
            <path
              d="M310 260 C 324 240, 318 210, 295 200 C 300 220, 284 232, 292 258 Z"
              fill="url(#flame)"
              opacity="0.9"
            />
            <path
              d="M290 250 C 300 232, 294 210, 272 202 C 278 220, 262 232, 276 250 Z"
              fill="url(#flame)"
              opacity="0.75"
            />
            <path
              d="M355 200 C 370 180, 366 150, 340 145 C 348 165, 334 178, 342 202 Z"
              fill="url(#flame)"
              opacity="0.6"
              style={{ animation: "creep-flame-b 2.9s ease-in-out infinite" }}
            />
          </g>
        </g>

        {/* -------------------- Body group -------------------- */}
        <g style={{ transformOrigin: "200px 260px", animation: poseAnim.bodyAnim }}>
          {/* Twin curly tails behind */}
          <g style={{ transformOrigin: "150px 300px", animation: `creep-tail ${poseAnim.tailDur} ease-in-out infinite alternate` }}>
            <path
              d="M150 320 C 120 310, 100 280, 108 245 C 112 225, 128 220, 136 235 C 122 246, 128 265, 148 268"
              stroke="#0a0714"
              strokeWidth="20"
              strokeLinecap="round"
              fill="none"
            />
            {/* fluff tip */}
            <path
              d="M108 240 C 100 232, 108 218, 122 220 C 118 228, 128 234, 122 244 Z"
              fill="#0a0714"
            />
          </g>
          <g style={{ transformOrigin: "250px 300px", animation: `creep-tail-b ${parseFloat(poseAnim.tailDur) + 0.2}s ease-in-out infinite alternate` }}>
            <path
              d="M250 320 C 280 310, 300 280, 292 245 C 288 225, 272 220, 264 235 C 278 246, 272 265, 252 268"
              stroke="#0a0714"
              strokeWidth="20"
              strokeLinecap="round"
              fill="none"
            />
            <path
              d="M292 240 C 300 232, 292 218, 278 220 C 282 228, 272 234, 278 244 Z"
              fill="#0a0714"
            />
          </g>

          {/* Cloak (behind body, if unlocked) */}
          {accessories.includes("cloak") && (
            <path
              d="M115 260 C 130 220, 170 210, 200 210 C 230 210, 270 220, 285 260 L 300 340 C 240 348, 160 348, 100 340 Z"
              fill="url(#scarf-grad)"
              opacity="0.9"
              style={{ filter: "drop-shadow(0 0 12px rgba(91,46,255,0.6))" }}
            />
          )}

          {/* Snowboard */}
          {accessories.includes("snowboard") && (
            <g transform="translate(310 260) rotate(30)">
              <rect x="-8" y="-56" width="16" height="112" rx="8" fill="url(#board-grad)" stroke="rgba(255,255,255,0.4)" strokeWidth="1" />
              <rect x="-5" y="-46" width="10" height="90" rx="4" fill="rgba(255,255,255,0.14)" />
            </g>
          )}

          {/* --- Fluffy body outline (chest tuft with jagged edges) --- */}
          {/* Outer fluff */}
          <path
            d="M138 235
               C 120 260, 108 300, 118 340
               L 132 355 L 128 340 L 148 355 L 148 340
               L 168 358 L 168 342 L 188 358 L 190 342
               L 210 358 L 210 342 L 230 358 L 230 342
               L 250 358 L 252 342 L 272 355 L 272 340
               L 288 342 L 282 355
               C 292 300, 280 260, 262 235 Z"
            fill="url(#body-grad)"
          />

          {/* Chest fluff highlights (V shape) */}
          <path
            d="M170 255 C 190 275, 210 275, 230 255 L 224 270 L 210 260 L 200 275 L 190 260 L 176 270 Z"
            fill="rgba(91,46,255,0.15)"
          />

          {/* Front paws visible at bottom */}
          <g>
            <ellipse cx="158" cy="352" rx="18" ry="12" fill="#050308" />
            <ellipse cx="242" cy="352" rx="18" ry="12" fill="#050308" />
            {/* toe beans */}
            <circle cx="152" cy="354" r="1.6" fill="rgba(214,0,47,0.5)" />
            <circle cx="158" cy="356" r="1.6" fill="rgba(214,0,47,0.5)" />
            <circle cx="164" cy="354" r="1.6" fill="rgba(214,0,47,0.5)" />
            <circle cx="236" cy="354" r="1.6" fill="rgba(214,0,47,0.5)" />
            <circle cx="242" cy="356" r="1.6" fill="rgba(214,0,47,0.5)" />
            <circle cx="248" cy="354" r="1.6" fill="rgba(214,0,47,0.5)" />
          </g>

          {/* Scarf (over body base) */}
          {accessories.includes("scarf") && (
            <g style={{ transformOrigin: "200px 240px" }}>
              <path
                d="M148 240 Q 200 262 252 240 L 248 262 Q 200 278 152 262 Z"
                fill="url(#scarf-grad)"
                stroke="rgba(255,255,255,0.32)"
                strokeWidth="0.8"
              />
              <path d="M240 260 L 236 300 L 254 300 L 250 260 Z" fill="url(#scarf-grad)" />
            </g>
          )}

          {/* Necklace with spiral pendant */}
          <g>
            <path
              d="M150 250 Q 200 275 250 250"
              stroke="#2A124A"
              strokeWidth="5"
              fill="none"
              strokeLinecap="round"
            />
            {/* Purple beads */}
            {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => {
              const t = i / 8;
              const x = 150 + (250 - 150) * t;
              const y = 250 + Math.sin(Math.PI * t) * -18;
              return <circle key={i} cx={x} cy={y} r="3.6" fill="#5B2EFF" stroke="#2A124A" strokeWidth="0.5" />;
            })}
            {/* Pendant */}
            <g transform="translate(200 288)" style={{ transformOrigin: "0 0", animation: "creep-pendant 6s ease-in-out infinite" }}>
              <circle r="14" fill="url(#pendant)" stroke="#0a0714" strokeWidth="1.2" />
              <circle r="14" fill="none" stroke="#8A6BFF" strokeWidth="0.6" opacity="0.7" />
              {/* Spiral (Uchiha-like fan) — original design */}
              <g style={{ transformOrigin: "0 0", animation: "creep-tomoe 12s linear infinite" }}>
                <path
                  d="M0 -10 C 5 -10, 10 -5, 10 0 C 10 5, 5 10, 0 10 C -5 10, -8 6, -6 2 L -2 2 L -2 -1 C -2 -4, 2 -6, 4 -3 Z"
                  fill="#0a0714"
                />
                <circle cx="0" cy="0" r="2" fill="#D6002F" />
              </g>
            </g>
          </g>

          {/* -------------------- Head group -------------------- */}
          <g style={{ transformOrigin: "200px 150px", animation: "creep-head 6s ease-in-out infinite" }}>
            {/* Ears - LEFT */}
            <g style={{ transformOrigin: "128px 88px", animation: "creep-ear-l 5.4s ease-in-out infinite" }}>
              {/* Ear outer (with fluff tip) */}
              <path
                d="M100 150
                   C 96 120, 108 82, 130 50
                   C 138 58, 148 74, 156 96
                   C 158 108, 158 122, 154 138
                   C 140 148, 118 152, 100 150 Z"
                fill="#0a0714"
              />
              {/* Ear inner */}
              <path
                d="M118 138
                   C 116 118, 122 96, 132 78
                   C 138 88, 146 104, 148 122
                   C 140 132, 128 138, 118 138 Z"
                fill="url(#ear-inner)"
              />
              {/* Fluff tuft */}
              <path
                d="M104 118 C 92 108, 92 90, 106 82 C 108 96, 116 106, 120 122 Z"
                fill="#0a0714"
              />
            </g>
            {/* Ears - RIGHT */}
            <g style={{ transformOrigin: "272px 88px", animation: "creep-ear-r 5.7s ease-in-out infinite" }}>
              <path
                d="M300 150
                   C 304 120, 292 82, 270 50
                   C 262 58, 252 74, 244 96
                   C 242 108, 242 122, 246 138
                   C 260 148, 282 152, 300 150 Z"
                fill="#0a0714"
              />
              <path
                d="M282 138
                   C 284 118, 278 96, 268 78
                   C 262 88, 254 104, 252 122
                   C 260 132, 272 138, 282 138 Z"
                fill="url(#ear-inner)"
              />
              <path
                d="M296 118 C 308 108, 308 90, 294 82 C 292 96, 284 106, 280 122 Z"
                fill="#0a0714"
              />
            </g>

            {/* Head fluffy shape */}
            <path
              d="M110 168
                 C 110 128, 130 82, 200 78
                 C 270 82, 290 128, 290 168
                 C 290 210, 268 240, 200 244
                 C 132 240, 110 210, 110 168 Z"
              fill="url(#body-grad)"
            />

            {/* Small fluff bumps around head outline */}
            <path
              d="M108 176 C 100 178, 96 190, 106 194 C 100 200, 108 214, 118 210
                 C 124 226, 138 232, 148 224
                 M 292 176 C 300 178, 304 190, 294 194 C 300 200, 292 214, 282 210
                 C 276 226, 262 232, 252 224"
              stroke="#0a0714"
              strokeWidth="10"
              strokeLinecap="round"
              fill="none"
            />

            {/* Forehead V hairline */}
            <path
              d="M186 90 L 200 108 L 214 90"
              stroke="#050308"
              strokeWidth="3"
              fill="none"
              strokeLinecap="round"
            />

            {/* Sharingan-like six-point star on forehead */}
            <g transform="translate(200 118)" style={{ transformOrigin: "0 0", animation: "creep-tomoe 20s linear infinite" }}>
              <path
                d="M0 -14 L 3 -4 L 12 -3 L 5 3 L 8 12 L 0 7 L -8 12 L -5 3 L -12 -3 L -3 -4 Z"
                fill="#5B2EFF"
                opacity="0.95"
                style={{ filter: "drop-shadow(0 0 5px rgba(91,46,255,0.8))" }}
              />
              <circle r="3" fill="#8A6BFF" />
              <circle r="1.4" fill="#FFFFFF" opacity="0.9" />
            </g>

            {/* Cheek shadows */}
            <ellipse cx="148" cy="200" rx="14" ry="5" fill="rgba(91,46,255,0.14)" />
            <ellipse cx="252" cy="200" rx="14" ry="5" fill="rgba(91,46,255,0.14)" />

            {/* Eyebrow tufts */}
            <path d="M138 148 C 148 136, 168 138, 176 150" stroke="#050308" strokeWidth="5" fill="none" strokeLinecap="round" />
            <path d="M262 148 C 252 136, 232 138, 224 150" stroke="#050308" strokeWidth="5" fill="none" strokeLinecap="round" />

            {/* Goggles OR eyes */}
            {accessories.includes("goggles") ? (
              <g>
                <path d="M120 170 L 280 170 L 286 156 L 114 156 Z" fill="#0d0a20" stroke="#8A6BFF" strokeWidth="1.5" />
                <circle cx="158" cy="180" r="22" fill="#0d0a20" stroke="#8A6BFF" strokeWidth="1.5" />
                <circle cx="242" cy="180" r="22" fill="#0d0a20" stroke="#8A6BFF" strokeWidth="1.5" />
                <circle cx="152" cy="174" r="5" fill="rgba(138,107,255,0.6)" />
                <circle cx="236" cy="174" r="5" fill="rgba(138,107,255,0.6)" />
              </g>
            ) : (
              <g>
                {/* LEFT EYE */}
                <g style={{ transformOrigin: "158px 180px" }}>
                  <ellipse cx="158" cy="180" rx="26" ry="30" fill="url(#eye-glow)" opacity={poseAnim.eyeGlow} />
                  <g style={{ transformOrigin: "158px 180px", animation: eyesShut ? "none" : `creep-blink ${poseAnim.blink} ease-in-out infinite` }}>
                    {/* Eye white */}
                    <ellipse cx="158" cy="180" rx="20" ry="25" fill="#f8dadf" opacity="0.15" />
                    {/* Red iris */}
                    <ellipse cx="158" cy="180" rx="18" ry="23" fill="url(#red-eye)" filter="url(#soft-glow)" />
                    {/* Tomoe (3 dots rotating) */}
                    <g style={{ transformOrigin: "158px 180px", animation: "creep-tomoe 8s linear infinite" }}>
                      <path d="M 158 163 C 156 166 156 170 160 170 C 158 168 158 165 158 163 Z" fill="#0a0210" />
                      <path d="M 173 186 C 170 184 166 186 168 190 C 168 187 172 187 173 186 Z" fill="#0a0210" />
                      <path d="M 143 186 C 146 184 150 186 148 190 C 148 187 144 187 143 186 Z" fill="#0a0210" />
                    </g>
                    {/* Slit pupil */}
                    <path d="M 158 168 C 155 175, 155 185, 158 192 C 161 185, 161 175, 158 168 Z" fill="#0a0210" />
                    <path d="M 158 172 C 156 176, 156 184, 158 188 C 160 184, 160 176, 158 172 Z" fill="#3a0010" />
                    {/* Highlight */}
                    <circle cx="153" cy="172" r="3.6" fill="#FFFFFF" opacity="0.95" />
                    <circle cx="164" cy="188" r="1.6" fill="#FFFFFF" opacity="0.65" />
                  </g>
                  {eyesShut && (
                    <path d="M138 180 Q 158 190 178 180" stroke="#8A6BFF" strokeWidth="3" fill="none" strokeLinecap="round" />
                  )}
                </g>

                {/* RIGHT EYE */}
                <g style={{ transformOrigin: "242px 180px" }}>
                  <ellipse cx="242" cy="180" rx="26" ry="30" fill="url(#eye-glow)" opacity={poseAnim.eyeGlow} />
                  <g style={{ transformOrigin: "242px 180px", animation: eyesShut ? "none" : `creep-blink ${poseAnim.blink} ease-in-out infinite` }}>
                    <ellipse cx="242" cy="180" rx="20" ry="25" fill="#f8dadf" opacity="0.15" />
                    <ellipse cx="242" cy="180" rx="18" ry="23" fill="url(#red-eye)" filter="url(#soft-glow)" />
                    <g style={{ transformOrigin: "242px 180px", animation: "creep-tomoe 8s linear infinite reverse" }}>
                      <path d="M 242 163 C 240 166 240 170 244 170 C 242 168 242 165 242 163 Z" fill="#0a0210" />
                      <path d="M 257 186 C 254 184 250 186 252 190 C 252 187 256 187 257 186 Z" fill="#0a0210" />
                      <path d="M 227 186 C 230 184 234 186 232 190 C 232 187 228 187 227 186 Z" fill="#0a0210" />
                    </g>
                    <path d="M 242 168 C 239 175, 239 185, 242 192 C 245 185, 245 175, 242 168 Z" fill="#0a0210" />
                    <path d="M 242 172 C 240 176, 240 184, 242 188 C 244 184, 244 176, 242 172 Z" fill="#3a0010" />
                    <circle cx="237" cy="172" r="3.6" fill="#FFFFFF" opacity="0.95" />
                    <circle cx="248" cy="188" r="1.6" fill="#FFFFFF" opacity="0.65" />
                  </g>
                  {eyesShut && (
                    <path d="M222 180 Q 242 190 262 180" stroke="#8A6BFF" strokeWidth="3" fill="none" strokeLinecap="round" />
                  )}
                </g>
              </g>
            )}

            {/* Nose (small triangular pink) */}
            <path
              d="M194 215 L 206 215 L 200 222 Z"
              fill="#D6002F"
              opacity="0.9"
            />

            {/* Mouth (small w) */}
            {reaction === "meow" ? (
              <path
                d="M 200 222 L 200 227 Q 194 234 190 230 M 200 227 Q 206 234 210 230"
                stroke="#050308"
                strokeWidth="2.4"
                fill="none"
                strokeLinecap="round"
              />
            ) : pose === "happy" ? (
              <path
                d="M 200 222 L 200 226 Q 192 234 186 228 M 200 226 Q 208 234 214 228"
                stroke="#050308"
                strokeWidth="2.4"
                fill="none"
                strokeLinecap="round"
              />
            ) : (
              <path
                d="M 200 222 L 200 226 Q 194 230 189 228 M 200 226 Q 206 230 211 228"
                stroke="#050308"
                strokeWidth="2.4"
                fill="none"
                strokeLinecap="round"
              />
            )}

            {/* Whiskers */}
            <g stroke="rgba(138,107,255,0.55)" strokeWidth="1.4" strokeLinecap="round">
              <line x1="106" y1="216" x2="140" y2="220" />
              <line x1="106" y1="226" x2="140" y2="224" />
              <line x1="294" y1="216" x2="260" y2="220" />
              <line x1="294" y1="226" x2="260" y2="224" />
            </g>
          </g>
        </g>

        {/* ZZZ */}
        {showZzz && (
          <g transform="translate(280 100)">
            <text x="0" y="0" fill="#8A6BFF" fontSize="22" fontFamily="Outfit, sans-serif" fontWeight="800" style={{ animation: "creep-zzz 3.2s ease-in-out infinite" }}>Z</text>
            <text x="16" y="-20" fill="#5B2EFF" fontSize="16" fontFamily="Outfit, sans-serif" fontWeight="800" style={{ animation: "creep-zzz 3.2s ease-in-out infinite 0.6s" }}>z</text>
            <text x="28" y="-38" fill="#5B2EFF" fontSize="11" fontFamily="Outfit, sans-serif" fontWeight="800" style={{ animation: "creep-zzz 3.2s ease-in-out infinite 1.2s" }}>z</text>
          </g>
        )}
      </svg>

      {/* Meow bubble */}
      {reaction === "meow" && (
        <div
          data-testid="creep-meow-bubble"
          className="absolute top-[4%] left-[62%] px-2.5 py-1 rounded-2xl rounded-bl-sm text-[11px] font-semibold text-white pointer-events-none"
          style={{
            background: "rgba(91,46,255,0.9)",
            boxShadow: "0 0 22px rgba(91,46,255,0.65)",
            animation: "creep-pop 0.9s ease-out forwards",
          }}
        >
          ¡Nyaa!
        </div>
      )}
    </div>
  );
};

const COLORS = ["#5B2EFF", "#8A6BFF", "#D6002F"];
function buildParticles(burst = false) {
  const count = burst ? 24 : 14;
  return Array.from({ length: count }).map((_, i) => ({
    x: 14 + Math.random() * 72,
    y: 8 + Math.random() * 74,
    s: 2 + Math.random() * (burst ? 5 : 4),
    c: COLORS[i % COLORS.length],
    o: 0.5 + Math.random() * 0.4,
    d: 3 + Math.random() * 5,
    delay: Math.random() * 2,
    dir: i % 2 === 0 ? "a" : "b",
  }));
}

export default CreepCharacter;
